import { useState } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import {
  bulkCheckServiceCodes,
  bulkAddServices,
} from "../../services/services";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";

const BulkUploadServices = () => {
  const [csvData, setCsvData] = useState([]);
  const [duplicateCodes, setDuplicateCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState([]);
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // CSV HEADERS
  const headers = [
    "name",
    "serviceCode",
    "sacCode",
    "baseCost",
    "gstRate",
    "category",
    "details",
    "description",
  ];

  // SAMPLE CSV DOWNLOAD
  const downloadSampleCSV = () => {
    const sampleRow = [
      "Website Development",
      "SRV001",
      "998314",
      "5000",
      "18",
      "IT Services",
      "Basic website development",
      "Includes hosting and setup",
    ];

    const csvContent = headers.join(",") + "\n" + sampleRow.join(",");
    const blob = new Blob([csvContent], { type: "text/csv" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sample_services.csv";
    link.click();
  };

  // DRAG DROP
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = () => setIsDragging(true);
  const handleDragLeave = () => setIsDragging(false);

  // FILE UPLOAD
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv") {
      toast.error("Only CSV files allowed!");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
        setShowUploadInfo(true);
        toast.success("CSV loaded successfully");
      },
    });
  };

  // SUBMIT
  const handleSubmit = async () => {
    try {
      const codes = csvData.map((s) => s.serviceCode);

      const res = await bulkCheckServiceCodes({ codes });

      const duplicates = (res.data.duplicates || []).map((d) =>
        typeof d === "string" ? d.trim() : d.serviceCode.trim(),
      );

      setDuplicateCodes(duplicates);

      const validServices = csvData.filter(
        (s) => !duplicates.includes(s.serviceCode?.trim()),
      );

      if (!validServices.length) {
        toast.error("All services already exist");
        return;
      }

      await bulkAddServices(validServices);

      setSavedCodes(validServices.map((s) => s.serviceCode.trim()));

      toast.success(`${validServices.length} Services uploaded successfully`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }
  };

  return (
    <div id="main" className="p-3">

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 custom-title">Bulk Upload Services</h4>

        <button className="btn btn-primary" onClick={downloadSampleCSV}>
          <i className="bi bi-download"></i>
          <span className="d-none d-md-inline ms-1">Sample CSV</span>
        </button>
      </div>

      {/* UPLOAD AREA */}
      <div className="row g-4 mt-4 px-3">
        <div className="col-md-4">
          <label
            htmlFor="csvInput"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className="border-dashed p-5 rounded text-center d-flex justify-content-center flex-column"
            style={{
              cursor: "pointer",
              background: isDragging ? "#E3F2FD" : "#F8F9FA",
              border: isDragging ? "2px dashed #0d6efd" : "2px dashed #b4b4b4",
              height: "20vh",
              transition: "0.2s",
            }}
          >
            <h5>
              <i className="bi bi-upload"></i> Upload CSV File
            </h5>
            <p className="text-muted">
              {isDragging ? "Drop file here" : "Click CSV file"}
            </p>
          </label>

          <input
            id="csvInput"
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileUpload}
          />
        </div>
      </div>

      {/* INFO MESSAGE */}
      {showUploadInfo && (
        <div className="alert alert-warning mt-3">
          Download <b>Sample CSV file</b> and keep column headings unchanged
          while uploading.
        </div>
      )}

      {/* PREVIEW */}
      {csvData.length > 0 && (
        <div className="mt-4">

          <div className="d-flex justify-content-between">
            <h5>Preview Services ({csvData.length})</h5>
          </div>

          <div className="table-responsive mt-3">
            <table className="table table-bordered text-nowrap">

              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Service Code</th>
                  <th>SAC</th>
                  <th>Price</th>
                  <th>GST Rate</th>
                  <th>Category</th>
                  <th>Details</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {csvData.map((item, index) => {

                  const code = item.serviceCode?.trim();
                  const isDuplicate = duplicateCodes.includes(code);
                  const isSaved = savedCodes.includes(code);

                  return (
                    <tr key={index}>

                      <td>{item.name}</td>
                      <td>{item.serviceCode}</td>
                      <td>{item.sacCode}</td>  
                      <td>{item.baseCost}</td>
                      <td>{item.gstRate}</td>
                      <td>{item.category}</td>
                      <td>{item.details}</td>
                      <td>{item.description}</td>

                      <td>
                        {isDuplicate && (
                          <span className="text-danger fw-bold">
                            Already Exists
                          </span>
                        )}

                        {isSaved && (
                          <span className="text-success fw-bold">
                            Saved
                          </span>
                        )}

                        {!isDuplicate && !isSaved && (
                          <span className="text-muted">
                            Pending
                          </span>
                        )}
                      </td>

                    </tr>
                  );
                })}
              </tbody>

            </table>

            <div className="d-flex justify-content-end mt-2">
              <button className="btn btn-primary" onClick={handleSubmit}>
                Submit
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default headerLayoutWrapper(BulkUploadServices);